import React, { useState } from "react";
import { api } from "../../../lib/api";
import { D, JAKARTA } from "../ui";
import { Plus, X } from "lucide-react";

// Modal tambah aset baru — bid dibuat otomatis dari nama (slug)
export function AssetNewModal({ token, rows, onClose, onCreated, defaultCategory = "Publik" }) {
  const PROMPT_DRIVEN = ["Alam", "Karakter", "Kendaraan"];
  const initCompact = PROMPT_DRIVEN.includes(defaultCategory);
  const [form, setForm] = useState({ name: "", category: defaultCategory, w: initCompact ? 1 : 3, h: initCompact ? 1 : 3, floors: initCompact ? 0 : 1, wall: "#d2c3a8", roof: "#aa4637", desc: "", jenis_sponsor: "lahan" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const cats = [...new Set([...rows.map(r => r.category), "Karakter", "Kendaraan"])];
  const promptDriven = PROMPT_DRIVEN.includes(form.category);
  const hex2rgb = (hx) => [parseInt(hx.slice(1, 3), 16), parseInt(hx.slice(3, 5), 16), parseInt(hx.slice(5, 7), 16)];
  const bid = form.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const clash = bid && rows.some(r => r.bid === bid);

  async function submit(e) {
    e.preventDefault();
    if (!bid || clash) { setMsg(clash ? `ID "${bid}" sudah dipakai aset lain` : "Nama tidak valid"); return; }
    setBusy(true);
    setMsg("");
    try {
      const payload = { bid, name: form.name.trim(), category: form.category.trim() || "Lainnya", desc: form.desc.trim() };
      if (promptDriven) {
        payload.w = 1; payload.h = 1; payload.floors = 0;
      } else {
        payload.w = +form.w; payload.h = +form.h; payload.floors = +form.floors;
        payload.wall = hex2rgb(form.wall); payload.roof = hex2rgb(form.roof);
      }
      if (form.category === "Sponsor") payload.jenis_sponsor = form.jenis_sponsor;   // billboard → panel iklan + lampu engine otomatis
      await api.createBuilding(token, payload);
      onCreated(bid);
    } catch (err) { setMsg(err.message); }
    setBusy(false);
  }

  const lbl = { display: "block", fontSize: 10.5, fontWeight: 800, color: D.sub, letterSpacing: 1.2, textTransform: "uppercase", margin: "0 0 5px 2px" };
  const inp = { width: "100%", padding: "10px 14px", background: "#fff", border: "1.5px solid #ece9dc", borderRadius: 14, fontSize: 13, color: "#141414", outline: "none", fontFamily: JAKARTA };

  return (
    <div data-testid="asset-new-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(20,20,20,0.35)", backdropFilter: "blur(4px)", display: "grid", placeItems: "center", padding: 20 }}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        style={{ width: "min(460px, 100%)", maxHeight: "88vh", overflowY: "auto", background: "#f8f6ee", borderRadius: 26, padding: "22px 24px", boxShadow: "0 24px 70px rgba(20,20,20,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ width: 34, height: 34, borderRadius: 12, background: D.blueSoft, display: "grid", placeItems: "center" }}>
            <Plus size={16} color={D.blue} />
          </span>
          <b style={{ flex: 1, fontSize: 18, fontWeight: 800, color: D.ink }}>Tambah Aset Baru</b>
          <button type="button" data-testid="new-close" onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: "50%", border: "1.5px solid #e3dfd0", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink }}>
            <X size={15} />
          </button>
        </div>

        <p style={lbl}>Nama Aset</p>
        <input data-testid="new-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="mis. Rumah Makan Padang" required style={inp} />
        <p style={{ margin: "5px 2px 12px", fontSize: 10.5, color: clash ? "#dc2626" : "#a8a496" }}>
          ID otomatis: <b>{bid || "..."}</b>{clash && " — sudah dipakai!"}
        </p>

        <p style={lbl}>Kategori</p>
        <select data-testid="new-category" value={form.category} onChange={e => {
          const c = e.target.value;
          const pd = PROMPT_DRIVEN.includes(c);
          setForm(f => ({ ...f, category: c, w: pd ? 1 : (f.w < 2 ? 3 : f.w), h: pd ? 1 : (f.h < 2 ? 3 : f.h), floors: pd ? 0 : (f.floors < 1 ? 1 : f.floors) }));
        }}
          style={{ ...inp, marginBottom: 12, cursor: "pointer" }}>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {form.category === "Sponsor" && (
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Jenis aset sponsor</label>
            <select data-testid="new-jenis-sponsor" value={form.jenis_sponsor} onChange={e => setForm(f => ({ ...f, jenis_sponsor: e.target.value }))} style={inp}>
              <option value="lahan">Lahan / papan (kartu + tombol IG)</option>
              <option value="billboard">Billboard berdiri (panel iklan + lampu otomatis)</option>
              <option value="billboard_atap">Billboard atap gedung (panel iklan + lampu otomatis)</option>
            </select>
          </div>
        )}

        {promptDriven ? (
          <p data-testid="prompt-driven-note" style={{ margin: "0 2px 12px", fontSize: 11, color: "#a8a496" }}>
            Kategori ini mengikuti master prompt — ukuran, lantai, dan warna tidak perlu diisi.
          </p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              {[["Lebar (w)", "w", 1, 6], ["Panjang (h)", "h", 1, 6], ["Lantai", "floors", 0, 12]].map(([label, key, mn, mx]) => (
                <span key={key} style={{ flex: 1 }}>
                  <p style={lbl}>{label}</p>
                  <input data-testid={`new-${key}`} type="number" min={mn} max={mx} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inp} />
                </span>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              {[["Warna Dinding", "wall"], ["Warna Atap", "roof"]].map(([label, key]) => (
                <span key={key} style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                  <input data-testid={`new-${key}`} type="color" value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}
                    style={{ width: 40, height: 40, border: "1.5px solid #ece9dc", borderRadius: 12, background: "#fff", cursor: "pointer", padding: 2 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: D.ink }}>{label}<span style={{ display: "block", fontSize: 10, color: "#a8a496" }}>{form[key]}</span></span>
                </span>
              ))}
            </div>
          </>
        )}

        <p style={lbl}>Deskripsi (opsional)</p>
        <input data-testid="new-desc" value={form.desc} onChange={e => setForm({ ...form, desc: e.target.value })} placeholder="mis. Rumah makan khas dengan atap gonjong" style={{ ...inp, marginBottom: 16 }} />

        <button data-testid="new-submit" type="submit" disabled={busy || !bid || !!clash}
          style={{ width: "100%", padding: 13, background: busy || !bid || clash ? "#b9b6a6" : D.blue, color: "#fff", border: 0, borderRadius: 99, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: JAKARTA, transition: "opacity 0.15s" }}>
          {busy ? "Menyimpan..." : "Buat Aset"}
        </button>
        {msg && <p data-testid="new-msg" style={{ color: "#dc2626", fontSize: 12.5, marginTop: 10, marginBottom: 0, fontWeight: 600 }}>{msg}</p>}
      </form>
    </div>
  );
}
